import React, { useState } from 'react';

import { invokeCurrentPlanningAction } from '../../services/planningActionRegistry';
import { getTopUpCreditAmount } from '../../utils/creditGate';
import { BillingModal } from '../BillingModal';
import { refreshPlanningActions } from '../planningActionsRefresh';

import { ActionButtonLabel } from './ActionBarActions';
import type { PressFix, PressFixKind, PressRefusal } from './pressRefusal';
import './PressRefusalNote.css';

interface BillingView {
  view: 'credits' | 'plans';
  credits?: number;
}

const FIX_CLASS: Partial<Record<PressFixKind, string>> = {
  add_credits: 'ai-overview-action-btn--add-credit',
  upgrade: 'ai-overview-action-btn--upgrade',
};

const fixClassName = (fix: PressFix): string => (
  `ai-overview-action-btn ${FIX_CLASS[fix.kind] ?? 'ai-overview-action-btn--press-fix'}`
);

const billingFor = (fix: PressFix): BillingView | null => {
  if (fix.kind === 'add_credits') return { view: 'credits', credits: fix.credits };

  return fix.kind === 'upgrade' ? { view: 'plans' } : null;
};

/** Press the gate control that runs the step on screen; with none current, reload the actions so it appears. */
const runPrerequisite = (projectId: string, fix: PressFix): void => {
  void invokeCurrentPlanningAction(projectId, fix.labels ?? [fix.label]).then((pressed) => {
    if (!pressed) refreshPlanningActions(projectId);
  });
};

/** The fixes the note carries out itself; the rest (retry, resolve_tasks) belong to the refused button. */
const runSharedFix = (fix: PressFix, projectId: string | null | undefined): void => {
  if (!projectId) return;
  if (fix.kind === 'refresh_actions') refreshPlanningActions(projectId);
  if (fix.kind === 'prerequisite') runPrerequisite(projectId, fix);
};

/** The reason, and the steps that come first in order, in the bar's action-error row. */
const RefusalText: React.FC<{ refusal: PressRefusal }> = ({ refusal }) => (
  <span className="ai-overview-action-error ai-overview-press-refusal" data-action-layout-message="true"
    data-press-refusal={refusal.code} role="alert">
    {refusal.message}
    {refusal.steps?.map((step, index) => (
      <span key={step.key} className="ai-overview-press-refusal__step" data-press-step={step.key}>
        {` ${index + 1}. ${step.summary}`}
      </span>
    ))}
  </span>
);

/**
 * Why a press started nothing, as a row under the bar's controls, and the one control that fixes it.
 * Both live inside the bar, so the sticky dock mirrors them (it copies `role="alert"` rows and buttons).
 * Credit fixes open the purchase form here; a listed step presses the gate button that runs it.
 */
export const PressRefusalNote: React.FC<{
  refusal: PressRefusal | null;
  projectId?: string | null;
  /** Fixes a control already in this bar covers (a credit gate has its own Add Credits). */
  omitFix?: PressFixKind[];
  /** Told of every fix chosen; carries out the ones only the refused button can: retry, resolve_tasks. */
  onFix?: (fix: PressFix) => void;
}> = ({ refusal, projectId, omitFix, onFix }) => {
  const [billing, setBilling] = useState<BillingView | null>(null);
  const fix = refusal?.fix && !omitFix?.includes(refusal.fix.kind) ? refusal.fix : undefined;
  const runFix = (chosen: PressFix) => {
    const view = billingFor(chosen);

    if (view) return setBilling(view);
    runSharedFix(chosen, projectId);

    return onFix?.(chosen);
  };

  if (!refusal && !billing) return null;

  return (
    <>
      {refusal && <RefusalText refusal={refusal} />}
      {refusal && fix && <button type="button" className={fixClassName(fix)} data-press-fix={fix.kind}
        onClick={() => runFix(fix)}><ActionButtonLabel label={fix.label} /></button>}
      {billing && <BillingModal source="planning:press-refusal" initialView={billing.view}
        initialCreditAmount={billing.view === 'credits' ? getTopUpCreditAmount({ shortfall: billing.credits }) : undefined}
        onClose={() => setBilling(null)} />}
    </>
  );
};
